import { gql } from 'graphql-tag'

/**
 * /company-cases — the caseload store's operations (stores/casesStore.js).
 * Moved out of the store's actions in the 2026-10-05 refactor, unchanged.
 */

/** A company's caseload with everything the list + the notes header show. The
 * four collections come batched (keystone services/case-collections.logic). */
export const CASELOAD_QUERY = gql`
  query GetCasesForCompany(
    $companyId: ID!
    $filters: CaseFiltersInput
    $viewAsUserId: ID
  ) {
    getCasesForCompany(
      companyId: $companyId
      filters: $filters
      viewAsUserId: $viewAsUserId
    ) {
      id
      title
      generalNotes
      motherName
      fatherName
      otherContactName
      contactPhone
      contactCallerId
      contactEmail
      contactLanguage
      homeAddress
      homeCity
      homeState
      homeZipCode
      schoolName
      schoolAddress
      schoolCity
      schoolState
      schoolZipCode
      therapyStartTime
      therapyEndTime
      availabilityHours
      availabilityNotes
      childName
      childDateOfBirth
      childAge
      childGender
      childLanguage
      # The other cases in this child's family — the list marks them
      # faintly when you select one of them (Sandy 2026-08-11). Empty
      # for almost every row; the virtual short-circuits without a
      # group and batches when there is one.
      siblings
      # The four collections come BATCHED (one query each for the whole
      # list, not one per case) under their usual names — same shape
      # (Sandy 2026-10-05; keystone services/case-collections.logic.ts).
      caseInsurances: insuranceLinks
      caseInsurancesNotes
      insuranceMemberId
      caseDiagnostics: diagnosisLinks
      caseDiagnosticsNotes
      caseStatus
      caseProgress
      caseType
      otherTherapy
      bcba { id firstName lastName nickName category }
      bcaba { id firstName lastName nickName category }
      rbt { id firstName lastName nickName category }
      bcbaNotes
      rbtNotes
      bcbaDriveTime
      bcabaDriveTime
      rbtDriveTime
      leadSource {
        id
        name
        shortName
        county
        areaCode
      }
      company {
        id
        name
        shortName
      }
      assignedTo {
        id
        name
        shortName
        label
        color
      }
      tags: tagLinks
      currentStage
      therapyPlace
      unreadTrackingCount
      checklist
      lastContact
      startedAt
      createdAt
      updatedAt
      myPlan
      initialDocuments
    }
  }
`

/** The same caseload, ids + unread only — the live badge refresh. */
export const CASELOAD_UNREAD_QUERY = gql`
  query GetCasesForCompany(
    $companyId: ID!
    $filters: CaseFiltersInput
  ) {
    getCasesForCompany(companyId: $companyId, filters: $filters) {
      id
      unreadTrackingCount
    }
  }
`

/** Set one entry of a case's checklist; resolves to the full checklist array. */
export const SET_CASE_CHECKLIST_STATUS_MUTATION = gql`
  mutation SetCaseChecklistStatus($caseId: ID!, $documentId: ID!, $status: String!) {
    setCaseChecklistStatus(caseId: $caseId, documentId: $documentId, status: $status)
  }
`

/** Save the case details modal. */
export const UPDATE_CASE_DETAILS_MUTATION = gql`
  mutation UpdateCase($caseId: ID!, $data: CaseUpdateInput!) {
    updateCase(where: { id: $caseId }, data: $data) {
      id
      title
      generalNotes
      motherName
      fatherName
      otherContactName
      contactPhone
      contactCallerId
      contactEmail
      contactLanguage
      homeAddress
      homeCity
      homeState
      homeZipCode
      schoolName
      schoolAddress
      schoolCity
      schoolState
      schoolZipCode
      therapyStartTime
      therapyEndTime
      availabilityHours
      availabilityNotes
      childName
      childDateOfBirth
      childAge
      childGender
      childLanguage
      caseStatus
      caseProgress
      caseType
      otherTherapy
      caseInsurancesNotes
      insuranceMemberId
      caseDiagnosticsNotes
      initialDocuments
      bcba { id firstName lastName nickName category }
      bcaba { id firstName lastName nickName category }
      rbt { id firstName lastName nickName category }
      bcbaNotes
      rbtNotes
      bcbaDriveTime
      bcabaDriveTime
      rbtDriveTime
      leadSource {
        id
        name
        shortName
        county
        areaCode
      }
      assignedTo {
        id
        name
        shortName
        label
        color
      }
      tags {
        assignedAt
        tag {
          id
          label
          category
          displayOrder
          icon
          color
        }
      }
      currentStage
      therapyPlace
      unreadTrackingCount
      checklist
      lastContact
      startedAt
      createdAt
      updatedAt
      myPlan
    }
  }
`

/** Move a case to another company ("Transfer To"). */
export const TRANSFER_CASE_MUTATION = gql`
  mutation TransferCase($caseId: ID!, $companyId: ID!) {
    updateCase(
      where: { id: $caseId }
      data: { company: { connect: { id: $companyId } } }
    ) {
      id
      company {
        id
        name
        shortName
      }
    }
  }
`

/** Assign the case's BCBA / BCaBA / RBT. */
export const UPDATE_CASE_STAFF_MUTATION = gql`
  mutation UpdateCase($caseId: ID!, $data: CaseUpdateInput!) {
    updateCase(where: { id: $caseId }, data: $data) {
      id
      bcba { id firstName lastName nickName category }
      bcaba { id firstName lastName nickName category }
      rbt { id firstName lastName nickName category }
      bcbaNotes
      rbtNotes
      bcbaDriveTime
      bcabaDriveTime
      rbtDriveTime
    }
  }
`
